import { CheckIcon, InfoIcon } from 'lucide-react'

import { cn } from 'cn'

import type { BadgeType } from '#admin/features/exhibitor/onboarding/api.ts'

/** What a pass is called on the desk. A type marked for exhibitors is a convention pass. */
export function passKind(type: Pick<BadgeType, 'category'>): string {
  return type.category === 'exhibitor_pass' ? 'Exhibitor pass' : 'Convention pass'
}

/** The access a pass gives. The tenant's own description comes first. */
export function passAccess(type: Pick<BadgeType, 'category' | 'description'>): string {
  const own = type.description?.trim() ?? ''
  if (own !== '') return own
  return type.category === 'exhibitor_pass'
    ? 'Access to the exhibition, for the stand crew only.'
    : 'Access to the conference and the exhibition.'
}

/**
 * The type that the stand's passes are issued as. With two or more types the operator selects
 * one. With one type, it shows which type the passes get, so the operator does not have to guess.
 */
export function PassTypePicker({
  types,
  value,
  onChange,
  error,
  disabled,
}: {
  types: readonly BadgeType[]
  value: string
  onChange: (id: string) => void
  error: string | undefined
  disabled: boolean
}) {
  const [only] = types
  if (types.length === 1 && only !== undefined) {
    return (
      <div className="flex flex-col gap-2">
        <PassCard type={only} checked />
        <p className="text-muted-foreground flex items-start gap-1.5 text-xs">
          <InfoIcon aria-hidden="true" className="mt-px size-3.5 shrink-0" />
          {`Each pass on these stands is issued as ${only.name}. To offer a convention pass too, mark a free ticket type "Exhibitors can get this pass" in Ticket types.`}
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <div
        role="radiogroup"
        aria-label="Pass type"
        aria-invalid={error !== undefined}
        className="grid gap-3 md:grid-cols-2"
      >
        {types.map((type) => (
          <PassCard
            key={type.id}
            type={type}
            checked={type.id === value}
            disabled={disabled}
            onSelect={() => onChange(type.id)}
          />
        ))}
      </div>
      {error === undefined ? null : (
        <p role="alert" className="text-destructive text-sm">
          {error}
        </p>
      )}
    </div>
  )
}

function PassCard({
  type,
  checked,
  disabled = false,
  onSelect,
}: {
  type: BadgeType
  checked: boolean
  disabled?: boolean
  /** Absent for the one type there is: it shows, and nothing is selected. */
  onSelect?: () => void
}) {
  const body = (
    <>
      <span
        aria-hidden="true"
        className={cn(
          'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border',
          checked ? 'border-primary bg-primary text-primary-foreground' : 'border-(--border)',
        )}
      >
        {checked ? <CheckIcon className="size-3.5" /> : null}
      </span>
      <span className="flex min-w-0 flex-col gap-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold">{type.name}</span>
          <span className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-[0.7rem] font-medium">
            {passKind(type)}
          </span>
        </span>
        <span className="text-muted-foreground text-xs">{passAccess(type)}</span>
      </span>
    </>
  )
  const look = cn(
    'flex items-start gap-3 rounded-lg border p-3 text-left',
    checked ? 'border-primary bg-primary/5 ring-primary/30 ring-1' : 'border-(--border)',
  )
  if (onSelect === undefined) return <div className={look}>{body}</div>
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        look,
        'focus-visible:ring-ring/50 transition-colors outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-60',
        checked ? '' : 'hover:bg-muted/50',
      )}
    >
      {body}
    </button>
  )
}
